<template>
  <div class="booking-status-list">
    <v-container fluid class="lighten-12 content">
      <ValidationObserver ref="createObserver" class="px-6">
        <tr v-if="editId == null">
          <th>
            <ValidationProvider
              v-slot="{ errors }"
              name="Name"
              vid="name"
              rules="required|no_spaces_only"
            >
              <v-col cols="12" class="pl-0">
                <v-text-field
                  :error-messages="errors"
                  required
                  dense
                  label="Name"
                  v-model="newItem.name"
                  outlined
                  @blur="newItem.name = newItem.name.trim()"
                ></v-text-field>
              </v-col>
            </ValidationProvider>
          </th>
          <th>
            <ValidationProvider
              v-slot="{ errors }"
              name="Shortname"
              vid="sortform"
              rules="required|no_spaces_only"
            >
              <v-col cols="12" class="pl-0">
                <v-text-field
                  :error-messages="errors"
                  required
                  dense
                  label="Shortname"
                  v-model="newItem.sortform"
                  outlined
                  @blur="newItem.sortform = newItem.sortform.trim()"
                ></v-text-field>
              </v-col>
            </ValidationProvider>
          </th>
          <th>
            <ValidationProvider
              v-slot="{ errors }"
              name="Color"
              vid="color_code"
              rules="required"
            >
              <v-col cols="12" class="pl-0">
                <ColorPicker v-model="newItem.color_code" />
                <div v-if="errors.length" class="error--text caption">
                  {{ errors[0] }}
                </div>
              </v-col>
            </ValidationProvider>
          </th>
          <th class="title">
            <v-col cols="4">
              <permission-control permissionName="Booking Status Create">
                <v-btn
                  @click="saveItem()"
                  depressed
                  small
                  class="text-white btn_blue btn_medium w-100"
                  >Create</v-btn
                >
              </permission-control>
            </v-col>
          </th>
        </tr>
      </ValidationObserver>

      <ValidationObserver ref="observer">
        <v-card class="lighten-12 card-content mb-2">
          <v-row class="ma-0 align-right">
            <TableFilters
              :filters="['status', 'search']"
              v-model="filter"
              :columns="columns"
            ></TableFilters>
          </v-row>
        </v-card>

        <LoadingComponent v-if="isLoading" :visible="isLoading"></LoadingComponent>
        <v-card class="lighten-12 mt-2">
          <v-container fluid class="light gray lighten-12">
            <v-row>
              <v-card-text class="pa-0">
                <v-data-table
                  :items-per-page="paginationOptions.perPage"
                  :fixed-header="true"
                  :headers="headers"
                  :items="items"
                  item-key="id"
                  hide-default-footer
                >
                  <template v-slot:no-data>
                    <img
                      style="height: 200px"
                      src="../../../../../assets/img/no-result.jpg"
                    />
                  </template>

                  <template v-slot:item.name="{ item }">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="Name"
                      vid="name"
                      v-if="item.id == editId"
                      rules="required|no_spaces_only"
                    >
                      <v-text-field
                        v-model="newItem.name"
                        :error-messages="errors"
                        outlined
                        class="pt-3"
                      ></v-text-field>
                    </ValidationProvider>
                    <p v-else>{{ item.name }}</p>
                  </template>

                  <template v-slot:item.sortform="{ item }">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="Shortname"
                      v-if="item.id == editId"
                      vid="sortform"
                      rules="required|no_spaces_only"
                    >
                      <v-text-field
                        :error-messages="errors"
                        v-model="newItem.sortform"
                        outlined
                        class="pt-3"
                      ></v-text-field>
                    </ValidationProvider>
                    <p v-else>{{ item.sortform }}</p>
                  </template>

                  <template v-slot:item.color_code="{ item }">
                    <ColorPicker
                      v-if="editId == item.id"
                      v-model="newItem.color_code"
                    />
                    <div v-else class="color-preview">
                      <span
                        class="color-swatch"
                        :style="{ backgroundColor: item.color_code }"
                      ></span>
                      <span class="caption">{{ item.color_code }}</span>
                    </div>
                  </template>

                  <template v-slot:item.status="{ item }">
                    <toggle-button
                      v-model="newItem.is_active"
                      v-if="editId == item.id"
                      :value="item.is_active"
                      :width="80"
                      :labels="{ checked: 'Active', unchecked: 'Archived' }"
                    ></toggle-button>
                    <v-chip
                      v-else
                      :x-small="true"
                      class="ma-2"
                      label
                      text-color="white"
                      :color="getStatusColor(item.is_active)"
                      dark
                      >{{ item.is_active ? "Active" : "Archived" }}</v-chip
                    >
                  </template>

                  <template v-slot:item.action="{ item }">
                    <v-menu offset-y transition="scroll-y-transition">
                      <template v-slot:activator="{ attrs, on }">
                        <v-icon
                          v-show="editId != item.id"
                          v-bind="attrs"
                          v-on="on"
                          >mdi-dots-vertical</v-icon
                        >
                        <v-btn
                          v-show="editId == item.id"
                          color="secondary"
                          fab
                          x-small
                          dark
                          class="ma-1"
                        >
                          <v-icon depressed small link @click="cancelEdit()"
                            >clear</v-icon
                          ></v-btn
                        >
                        <v-btn
                          v-show="editId == item.id"
                          color="green"
                          fab
                          x-small
                          dark
                          ><v-icon link @click="updateItem(item)"
                            >done</v-icon
                          ></v-btn
                        >
                      </template>

                      <v-list class="actions">
                        <permission-control permissionName="Booking Status Edit">
                          <v-list-item link @click="editItem(item)">
                            <span>
                              <v-icon>mdi-pencil-box-outline</v-icon>
                              Edit
                            </span>
                          </v-list-item>
                        </permission-control>
                        <permission-control
                          permissionName="Booking Status Soft Delete"
                        >
                          <v-list-item link @click="openConfirmation(item)">
                            <span>
                              <v-icon>
                                {{
                                  item.is_active
                                    ? "mdi-archive"
                                    : "mdi-checkbox-marked-circle"
                                }}
                              </v-icon>
                              {{ item.is_active ? "Archive" : "Active" }}
                            </span>
                          </v-list-item>
                        </permission-control>
                      </v-list>
                    </v-menu>
                  </template>

                  <template v-slot:footer>
                    <pagination
                      @paginationOptions="setPaginationOptions"
                      @response="setResponseData"
                      url="booking-statuses"
                      :filter="filter"
                      ref="pagination"
                    />
                  </template>
                </v-data-table>
              </v-card-text>
            </v-row>
          </v-container>
        </v-card>
      </ValidationObserver>
    </v-container>
  </div>
</template>

<script>
import TableFilters from "@/components/base/TableFilters";
import pagination from "../../../components/pagination";
import { required, max, email, regex } from "vee-validate/dist/rules";
import {
  extend,
  ValidationObserver,
  ValidationProvider,
  setInteractionMode,
} from "vee-validate";

setInteractionMode("eager");

extend("required", {
  ...required,
  message: "{_field_} is required",
});

extend("no_spaces_only", {
  validate: (value) => value && value.trim().length > 0,
  message: "{_field_} cannot be empty or spaces only",
});

extend("max", {
  ...max,
  message: "{_field_} may not be greater than {length} characters",
});

extend("email", {
  ...email,
  message: "Email must be valid",
});

extend("regex", {
  ...regex,
  message: "The value is not a valid phone number",
});

const defaultItem = () => ({
  name: "",
  sortform: "",
  color_code: "#3b5bdb",
  is_active: true,
});

export default {
  components: {
    pagination,
    ValidationProvider,
    TableFilters,
    ValidationObserver,
  },
  data() {
    return {
      paginationOptions: {},
      isLoading: false,
      items: [],
      columns: [
        {
          text: "Name",
          value: "name",
          show: true,
          disabled: false,
          width: "25%",
          align: "left",
        },
        {
          text: "Shortname",
          value: "sortform",
          show: true,
          disabled: false,
          width: "20%",
          align: "left",
        },
        {
          text: "Color",
          value: "color_code",
          show: true,
          disabled: false,
          width: "20%",
          align: "left",
        },
        {
          text: "Status",
          value: "status",
          show: true,
          disabled: false,
          align: "center",
          width: "15%",
        },
        {
          text: "Actions",
          value: "action",
          show: true,
          disabled: false,
          align: "center",
          width: "15%",
        },
      ],
      newItem: defaultItem(),
      filter: {
        search: "",
        status: "",
      },
      editId: null,
    };
  },
  computed: {
    headers() {
      return this.columns.filter((item) => item.show);
    },
  },
  methods: {
    setPaginationOptions(data) {
      this.paginationOptions = data;
    },
    refreshData() {
      this.$refs.pagination.refreshData();
    },
    setResponseData(data) {
      this.items = data;
    },
    cancelEdit() {
      this.editId = null;
      this.newItem = defaultItem();
      this.refreshData();
    },
    async saveItem() {
      const isValid = await this.$refs.createObserver.validate();
      if (!isValid) {
        return;
      }
      this.newItem.name = this.newItem.name.trim();
      this.newItem.sortform = this.newItem.sortform.trim();
      this.isLoading = true;
      this.$store
        .dispatch("BookingStatus/CreateBookingStatus", this.newItem)
        .then(() => {
          this.$toast.success("Booking status created successfully");
          this.refreshData();
          this.$refs.createObserver.reset();
          this.newItem = defaultItem();
          this.isLoading = false;
        })
        .catch((err) => {
          if (err && err.data && err.data.error) {
            this.$refs.createObserver.setErrors(err.data.error);
          }
          this.$toast.error("Booking status create failed");
          this.isLoading = false;
        });
    },
    async updateItem(data) {
      const isValid = await this.$refs.observer.validate();
      if (!isValid) {
        return;
      }
      const item = _.cloneDeep(data);
      if (item.name) item.name = item.name.trim();
      if (item.sortform) item.sortform = item.sortform.trim();
      this.$store
        .dispatch("BookingStatus/EditBookingStatus", item)
        .then(() => {
          this.editId = null;
          this.newItem = defaultItem();
          this.$toast.success("Booking status updated successfully");
          this.$refs.observer.reset();
          this.refreshData();
        })
        .catch((err) => {
          if (err && err.data && err.data.error) {
            this.$refs.observer.setErrors(err.data.error);
          }
          this.$toast.error("Booking status update failed");
        });
    },
    editItem(data) {
      this.newItem = { ...data };
      this.editId = data.id;
    },
    getStatusColor(isActive) {
      return isActive ? "green" : "gray";
    },
    openConfirmation(item) {
      this.$confirm(
        `Do you want to ${item.is_active ? "archive" : "activate"} ${item.name}?`
      ).then((res) => {
        if (res) {
          this.$store
            .dispatch("BookingStatus/ActiveOrArchiveBookingStatus", item.id)
            .then(() => {
              this.refreshData();
              const msg = item.is_active ? "archived" : "activated";
              this.$toast.success(`Successfully ${msg}`);
            })
            .catch(() => {
              this.$toast.error("Booking status status change failed");
            });
        }
      });
    },
  },
  created() {
    this.refreshData();
  },
};
</script>

<style scoped>
.color-preview {
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-swatch {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.12);
  flex-shrink: 0;
}
</style>
